import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

export default function MilestoneSummaryTable() {
  const pid = useSelector((state) => state.user.projectId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pid) return;

    const fetchData = async () => {
      try {
        const response = await fetch(
          `https://api-project360-insights.haqdarshak.com/milestone/project-milestone-summary/?pid=${pid}`
        );
        const result = await response.json();
        setData(result);
      } catch (error) {
        console.error("Error fetching milestone summary:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pid]);

  if (loading)
    return <div className="text-center p-4">Loading milestone summary...</div>;

  if (!data || !data.milestone_summary)
    return (
      <span className="p-4 mb-6 rounded-md ml-[1.5vw] bg-red-50 border-l-2 border-r-2 mt-[2vh] border-red-500 text-green-800">
        No Milestone Summary Found for this project
      </span>
    );

  // Get all possible fields dynamically
  const fieldNames = Object.keys(data.milestone_summary[0]?.targets || {});

  // Function to check if all milestones have 0 for a field
  const isAllZero = (field) =>
    data.milestone_summary.every((m) => (m.targets?.[field] || 0) === 0);

  // Only show non-zero fields
  const visibleFields = fieldNames.filter((f) => !isAllZero(f));

  // Map deliverable to its achieved metric key
  const achievedMapping = {
    "Schemes DS Citizen": "applications",
    "Schemes BR Citizen": "br",
    "DFL BR": "dfl",
    "Yojana Card BR": "yc",
  };

  return (
    <div className="p-6">
      {/* === Main Boundary with Heading === */}
      <div className="border border-gray-300 rounded-2xl shadow-md bg-white p-6">
        <div className="border-b border-gray-200 pb-3 mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Milestone Summary
          </h2>
        </div>

        {/* === Milestone Cards === */}
        <div className="space-y-8">
          {data.milestone_summary.map((m, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-xl p-5 bg-gray-50 shadow-sm"
            >
              {/* Milestone Header */}
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {m.milestone_name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Expected Completion:{" "}
                    <span className="font-medium text-gray-700">
                      {m.end_date || "—"}
                    </span>
                  </p>
                </div>
                <span
                  className={`px-3 py-1 text-sm rounded-lg font-medium ${
                    m.milestone_status === "Completed"
                      ? "bg-green-100 text-green-700"
                      : m.milestone_status === "In Progress"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {m.milestone_status || "N/A"}
                </span>
              </div>

              {/* Deliverables Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg bg-white">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold border-b border-gray-200">
                        Deliverables
                      </th>
                      <th className="px-4 py-2 text-center font-semibold border-b border-gray-200">
                        Target
                      </th>
                      <th className="px-4 py-2 text-center font-semibold border-b border-gray-200">
                        Achieved
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFields.map((field, idx) => {
                      const achievedKey = achievedMapping[field];
                      const achievedValue = achievedKey
                        ? m.achieved?.[achievedKey] || "-"
                        : "-";

                      return (
                        <tr
                          key={idx}
                          className={`${
                            idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                          } hover:bg-gray-100 transition`}
                        >
                          <td className="px-4 py-2 text-gray-800 text-sm border-b border-gray-100">
                            {field}
                          </td>
                          <td className="px-4 py-2 text-center text-gray-700 text-sm border-b border-gray-100">
                            {m.targets?.[field] || 0}
                          </td>
                          <td className="px-4 py-2 text-center text-gray-700 text-sm border-b border-gray-100">
                            {achievedValue}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
